const combineClassNames = (...classNames) => classNames.filter(Boolean).join(' ');

export const SKILLS_ICON_CLASS = 'bi-award';

const FRIENDLY_EDITOR_NODE_NAMES = Object.freeze({
  a: 'Link', article: 'Section', audio: 'Audio', blockquote: 'Quote', code: 'Code',
  div: 'Container', figcaption: 'Caption', figure: 'Image section', footer: 'Footer',
  h1: 'Main heading', h2: 'Section heading', h3: 'Subheading', h4: 'Heading',
  h5: 'Heading', h6: 'Heading', header: 'Introduction', i: 'Icon', img: 'Image',
  li: 'List item', main: 'Main content', nav: 'Navigation', ol: 'Numbered list',
  p: 'Paragraph', projects: 'Projects', section: 'Section', skills: 'Skills',
  span: 'Text', table: 'Table', ul: 'List', video: 'Video',
});

const FRIENDLY_COMPONENT_CLASSES = Object.freeze([
  ['btn', 'Button'],
  ['card', 'Card'],
  ['alert', 'Alert'],
  ['carousel', 'Carousel'],
  ['accordion', 'Accordion'],
  ['list-group', 'List'],
]);

export const getFriendlyEditorNodeName = (node) => {
  const classes = Array.isArray(node?.attributes?.class) ? node.attributes.class : [];
  const component = FRIENDLY_COMPONENT_CLASSES.find(([className]) => classes.includes(className));
  if (component) return component[1];
  return FRIENDLY_EDITOR_NODE_NAMES[node?.type] || 'Content';
};

export const getEditorItemClassName = ({ isFocused = false, isEditing = false, depth = 0 } = {}) => (
  combineClassNames(
    'edit-item',
    'html-edit-item',
    isFocused ? 'is-focused' : '',
    isEditing ? 'is-editing' : '',
    `html-edit-depth-${Math.min(Number(depth) || 0, 6)}`
  )
);

export const getToolbarButtonClassName = (extraClassName = '') => (
  combineClassNames('btn btn-light btn-outline-dark mb-1 mx-1 editor-toolbar-button', extraClassName)
);

export const getElementActionButtonClassName = ({ danger = false } = {}) => (
  combineClassNames(
    'btn btn-sm element-action-button',
    danger ? 'btn-outline-danger' : 'btn-light btn-outline-dark'
  )
);

export const getEditorNodeIdFromEvent = (event) => {
  const target = event?.target;
  if (!(target instanceof Element)) return null;
  return target.closest('[data-editor-node-id]')?.getAttribute('data-editor-node-id') || null;
};

export const getToggledEditorSelectionId = (currentNodeId, clickedNodeId) => {
  if (!clickedNodeId) return null;
  return currentNodeId === clickedNodeId ? null : clickedNodeId;
};

export const createSelectionToggleController = ({
  delay = 220,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
} = {}) => {
  let timerId = null;

  const cancel = () => {
    if (timerId === null) return;
    clearTimer(timerId);
    timerId = null;
  };

  return {
    cancel,
    schedule(action, clickCount = 1) {
      cancel();
      if (clickCount > 1) return false;
      timerId = setTimer(() => {
        timerId = null;
        action();
      }, delay);
      return true;
    },
  };
};

export const findEditorNodeElement = (nodeId, root = document) => {
  if (!nodeId || !root?.querySelector) return null;

  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return root.querySelector(`[data-editor-node-id="${CSS.escape(String(nodeId))}"]`);
  }

  for (const element of root.querySelectorAll?.('[data-editor-node-id]') || []) {
    if (element.getAttribute('data-editor-node-id') === String(nodeId)) return element;
  }
  return null;
};

export const isSelectionShortcutInput = (target) => {
  const tagName = String(target?.tagName || '').toLowerCase();
  return ['input', 'textarea', 'select'].includes(tagName) || Boolean(target?.isContentEditable);
};

export const isElementOutsideViewport = (element, viewport = {}) => {
  if (!element || typeof element.getBoundingClientRect !== 'function') return false;

  const rect = element.getBoundingClientRect();
  const width = Number(viewport.width) || document.documentElement.clientWidth || window.innerWidth;
  const height = Number(viewport.height) || document.documentElement.clientHeight || window.innerHeight;

  return rect.top < 0 || rect.left < 0 || rect.bottom > height || rect.right > width;
};

const viewportBounds = (viewport = {}) => {
  const visualViewport = typeof window !== 'undefined' ? window.visualViewport : null;
  const left = Number(viewport.left ?? visualViewport?.offsetLeft) || 0;
  const top = Number(viewport.top ?? visualViewport?.offsetTop) || 0;
  const width = Number(viewport.width ?? visualViewport?.width)
    || document.documentElement.clientWidth
    || window.innerWidth;
  const height = Number(viewport.height ?? visualViewport?.height)
    || document.documentElement.clientHeight
    || window.innerHeight;
  const suppliedRight = Number(viewport.right);
  const suppliedBottom = Number(viewport.bottom);
  return {
    left,
    top,
    right: Number.isFinite(suppliedRight) ? suppliedRight : left + width,
    bottom: Number.isFinite(suppliedBottom) ? suppliedBottom : top + height,
  };
};

export const getEditorActionViewport = (root = document) => {
  const controls = root?.querySelectorAll?.('.selection-navigator, .new-blog-button-container') || [];
  const visibleControlTops = [...controls]
    .map((control) => control.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0 && rect.bottom > 0)
    .map((rect) => rect.top);

  return visibleControlTops.length
    ? { bottom: Math.min(...visibleControlTops) }
    : {};
};

export const getViewportSnapOffset = (rect, viewport = {}, margin = 8) => {
  if (!rect) return { x: 0, y: 0 };

  const bounds = viewportBounds(viewport);
  const safeMargin = Math.max(0, Number(margin) || 0);
  const safeLeft = bounds.left + safeMargin;
  const safeTop = bounds.top + safeMargin;
  const safeRight = bounds.right - safeMargin;
  const safeBottom = bounds.bottom - safeMargin;
  let x = 0;
  let y = 0;

  if (rect.left < safeLeft) x = safeLeft - rect.left;
  else if (rect.right > safeRight) x = safeRight - rect.right;

  if (rect.top < safeTop) y = safeTop - rect.top;
  else if (rect.bottom > safeBottom) y = safeBottom - rect.bottom;

  return { x, y };
};

const combinedActionRect = (group) => {
  const surfaces = group.matches('.quick-element-actions')
    ? [group]
    : [...group.children].filter((child) => child.matches('.quick-element-actions'));
  const rects = (surfaces.length ? surfaces : [group])
    .map((surface) => surface.getBoundingClientRect());

  return rects.reduce((combined, rect) => ({
    top: Math.min(combined.top, rect.top),
    right: Math.max(combined.right, rect.right),
    bottom: Math.max(combined.bottom, rect.bottom),
    left: Math.min(combined.left, rect.left),
  }), {
    top: Number.POSITIVE_INFINITY,
    right: Number.NEGATIVE_INFINITY,
    bottom: Number.NEGATIVE_INFINITY,
    left: Number.POSITIVE_INFINITY,
  });
};

export const snapQuickActionsToViewport = (actionLayer, viewport = {}, margin = 8) => {
  if (!actionLayer) return false;

  const groups = actionLayer.querySelectorAll([
    '.quick-element-actions-top-center',
    '.quick-element-actions-drag',
    '.quick-element-actions-child',
    '.quick-element-actions-bottom-center',
  ].join(','));

  groups.forEach((group) => {
    group.style.setProperty('--quick-viewport-x', '0px');
    group.style.setProperty('--quick-viewport-y', '0px');
  });

  let snapped = false;
  groups.forEach((group) => {
    const offset = getViewportSnapOffset(combinedActionRect(group), viewport, margin);
    if (offset.x || offset.y) snapped = true;
    group.style.setProperty('--quick-viewport-x', `${offset.x}px`);
    group.style.setProperty('--quick-viewport-y', `${offset.y}px`);
  });
  return snapped;
};

export const getSelectionShortcutTarget = ({ key, altKey = false, context }) => {
  if (key === 'Escape') return { action: 'clear', nodeId: null };
  if (!altKey || !context) return null;

  const targetsByKey = {
    ArrowUp: context.parentId,
    ArrowDown: context.firstChildId,
    ArrowLeft: context.previousSiblingId,
    ArrowRight: context.nextSiblingId,
  };
  const nodeId = targetsByKey[key];
  return nodeId ? { action: 'select', nodeId } : null;
};
